import { FC, useCallback, useEffect, useState } from 'react';

import {
  CopyOutlined,
  ExclamationCircleOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { Button, Col, Row, Select, Spin, Switch, Tooltip } from 'antd';
import { Modal as AntModal } from 'antd';
import { useForm } from 'antd/es/form/Form';
import cn from 'classnames';
import Pagination from 'component/pagination';
import { useParams } from 'react-router-dom';
import { MY_EVENTS_ROUTE } from 'routes/events/list';
import { useDebounce } from 'tools/hooks/debounce.hook';
import { Dropdown, Form, Icon, Input, Label, Link, Modal, Typography, message } from 'ui';
import HorizontalScrollWithArrows from 'ui/horizontalScrollWithArrows';
import Table from 'ui/table/table';
import { formRules } from 'utils/formRules';

import { createEventFormActiveEventSelector } from 'selectors/eventsSlice.selector';
import {
  useCreatePriorityPassMutation,
  useDeletePriorityPassMutation,
  useEditPriorityPassMutation,
  useLazyGetEventDetailQuery,
  useLazyGetPriorityPassesQuery,
} from 'services/events/eventsApiService';
import { PRIORITY_PASS_STATUS_ENUM, PriorityPassType } from 'services/events/evetns.api.type';
import { setActiveEvent, setEditingEventId } from 'slices/eventsSlice';
import { useAppDispatch, useAppSelector } from 'store/index';

import EventLoadingState from 'containers/events-loading-state';

import { EventCreatedType } from 'types/events/eventTypes';

import s from './PriorityPass.module.scss';
import { PriorityPassFilterType, formatDate, initialFilter } from './priorityPass.util';

const { confirm } = AntModal;

const DROPDOWN_ITEMS = [
  { key: 'edit', label: <div className={cn(s.dropdownItem, 'no-row-click')}>Edit</div> },
  { key: 'delete', label: <div className={cn(s.dropdownItem, 'no-row-click')}>Delete</div> },
];

const toSentenceCase = (text?: string) =>
  text ? text.charAt(0).toUpperCase() + text.slice(1).toLowerCase() : '';

const PriorityPass: FC = () => {
  const { eventId } = useParams();
  const dispatch = useAppDispatch();

  const [form] = useForm();
  const [searchForm] = useForm();

  const activeEvent = useAppSelector(createEventFormActiveEventSelector);

  const searchEventName = Form.useWatch('term', searchForm);
  const debouncedSearchEventName = useDebounce(searchEventName, 500) as string;

  const [getEventDetail, { isFetching }] = useLazyGetEventDetailQuery();

  const [createPriorityPass, { isLoading: creating }] = useCreatePriorityPassMutation();
  const [deletePriorityPass] = useDeletePriorityPassMutation();
  const [editPriorityPass, { isLoading: editing }] = useEditPriorityPassMutation();
  const [getPriorityPasses, { data: fetchedData, isFetching: fetching }] =
    useLazyGetPriorityPassesQuery();
  const [requestFilter, setRequestFilter] = useState<PriorityPassFilterType>(initialFilter);
  const [statusFilter, setStatusFilter] = useState<PRIORITY_PASS_STATUS_ENUM>(
    PRIORITY_PASS_STATUS_ENUM.ALL,
  );

  const [editingPriorityPass, setEditingPriorityPass] = useState<PriorityPassType | null>(null);

  const [priorityPasses, setPriorityPasses] = useState<PriorityPassType[]>([]);
  const [open, setOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState<boolean>(false);
  const [priorityDetail, setPriorityDetail] = useState<PriorityPassType | null>(null);

  const ticketTypesOptions = activeEvent?.ticketTypes?.map((ticket) => ({
    label: ticket?.name,
    value: ticket?.id,
  }));

  const [openedDropdown, setOpenedDropdown] = useState<string | null>(null);
  const [openPanels, setOpenPanels] = useState<string[]>([]);

  const handleClickDropdownItems = (event: { key: string }, record: PriorityPassType) => {
    setOpenedDropdown(null);

    if (event.key === 'delete') {
      showConfirm(record?.id);
    }

    if (event.key === 'edit') {
      setEditingPriorityPass(record);
      setOpen(true);
    }
  };

  const handleCopy = async (id) => {
    const fullUrl = `${activeEvent?.basicInfo?.url}?prioritypass=${id}`;
    try {
      await navigator.clipboard.writeText(fullUrl);
      message.success('Copied to clipboard!');
    } catch (err) {
      message.error('Failed to copy.');
    }
  };

  const showedElements =
    Number(fetchedData?.priorityPasses?.length) +
    (Number(fetchedData?.pageNumber) - 1) * Number(requestFilter.pageSize);

  const handlePageChange = useCallback((pageNumber: number) => {
    setRequestFilter((prevState) => ({
      ...prevState,
      pageNumber,
    }));
  }, []);

  const handleGetEditingEvent = useCallback(
    async (eventId: string) => {
      const res = await getEventDetail(eventId);

      if ('data' in res) {
        const updatedData = {
          ...res.data,
          media: {
            ...res?.data?.media,
            flyer: res?.data?.media.flyer
              ? `${res?.data?.media.flyer}?v=${Math.random().toString(36).slice(2, 11)}`
              : null,
          },
        };
        dispatch(setActiveEvent(updatedData as EventCreatedType));
      }
    },
    [dispatch, getEventDetail],
  );

  useEffect(() => {
    if (eventId) {
      dispatch(setEditingEventId(eventId));
      handleGetEditingEvent(eventId);
    }
  }, [dispatch, eventId, handleGetEditingEvent]);

  const showModal = () => {
    setEditingPriorityPass(null);
    form.resetFields();
    setOpen(true);
  };
  const handleOk = () => {
    setOpen(false);
  };

  const handleCancel = () => {
    form.resetFields();
    setOpen(false);
  };

  const handleDetailOk = () => {
    setDetailOpen(false);
  };

  const handleDetailCancel = () => {
    setDetailOpen(false);
  };

  useEffect(() => {
    if (debouncedSearchEventName?.length >= 3 && eventId) {
      try {
        getPriorityPasses({
          id: eventId,
          term: debouncedSearchEventName,
          status: statusFilter,
          pageNumber: 1,
          pageSize: 10,
        }).then((res) => {
          if ('data' in res) {
            setPriorityPasses(res.data?.priorityPasses ?? []);
          }
        });
      } catch (error) {
        console.error('Error fetching priorityPasss:', error);
      }
    } else if (debouncedSearchEventName?.length === 0 && eventId) {
      try {
        getPriorityPasses({
          id: eventId,
          term: '',
          status: statusFilter,
          pageNumber: 1,
          pageSize: 10,
        }).then((res) => {
          if ('data' in res) {
            setPriorityPasses(res.data?.priorityPasses ?? []);
          }
        });
      } catch (error) {
        console.error('Error fetching priorityPasss:', error);
      }
    }
  }, [setRequestFilter, eventId, statusFilter, getPriorityPasses, debouncedSearchEventName]);

  useEffect(() => {
    setRequestFilter((prevState) => ({
      ...prevState,
      status: statusFilter,
    }));
  }, [setRequestFilter, statusFilter]);

  useEffect(() => {
    const fetchNotifications = async () => {
      if (eventId) {
        try {
          const res = await getPriorityPasses({ ...requestFilter, id: eventId });
          if ('data' in res) {
            setPriorityPasses(res.data?.priorityPasses ?? []);
          }
        } catch (error) {
          console.error('Error fetching priorityPasss:', error);
        }
      }
    };

    fetchNotifications();
  }, [getPriorityPasses, requestFilter, eventId]);

  const showConfirm = async (id: string) => {
    confirm({
      title: 'Do you want to delete this priority pass?',
      icon: <ExclamationCircleOutlined />,
      content: 'This action cannot be undone.',
      centered: true,
      mask: false,
      async onOk() {
        const res = await deletePriorityPass(id);

        if ('data' in res) {
          setPriorityPasses(res.data?.priorityPasses ?? []);
          message.success('Priority Pass item removed successfully.');
        }
      },
      onCancel() {
        console.log('Cancel');
      },
    });
  };

  useEffect(() => {
    if (form && editingPriorityPass && open) {
      form.setFieldValue('ticketTypes', editingPriorityPass?.ticketTypesAllowed[0].id);
      form.setFieldValue('qty', editingPriorityPass?.ticketTypesAllowed[0].qty);
      form.setFieldValue('neverExpires', editingPriorityPass?.neverExpires);
      form.setFieldValue('status', editingPriorityPass?.status);
    }
  }, [editingPriorityPass, open, form]);

  const handleSubmit = useCallback(
    async (data) => {
      if (data && eventId) {
        if (!editingPriorityPass) {
          const result = {
            eventId: eventId,
            email: data.email,
            fullName: data.fullName,
            neverExpires: data.neverExpires,
            ticketTypes: [
              {
                id: data.ticketTypes,
                qty: data.qty,
              },
            ],
          };
          const res = await createPriorityPass(result);
          if ('data' in res) {
            setOpen(false);
            setPriorityPasses(res.data?.priorityPasses ?? []);
            setStatusFilter(PRIORITY_PASS_STATUS_ENUM.ALL);
            message.success('Successfully created priority pass.');
          }
        } else {
          const result = {
            id: editingPriorityPass.id,
            status: data.status,
            neverExpires: data.neverExpires,
            ticketTypes: [
              {
                id: data.ticketTypes,
                qty: data.qty,
              },
            ],
          };
          const res = await editPriorityPass(result);
          if ('data' in res) {
            setOpen(false);
            setPriorityPasses(res.data?.priorityPasses ?? []);
            setStatusFilter(PRIORITY_PASS_STATUS_ENUM.ALL);
            message.success('Successfully updated priority pass.');
          }
        }
      }
    },
    [createPriorityPass, eventId, editPriorityPass, editingPriorityPass],
  );

  return (
    <div className={s.wrapper}>
      <Link
        to={MY_EVENTS_ROUTE}
        icon="arrowLeft"
        label="My Events"
        labelType="h6"
        className={s.backLink}></Link>
      <div className={s.row}>
        <div className={s.titileHeader}>
          {activeEvent?.basicInfo?.eventName && (
            <Typography type="h2" weight={700}>
              &quot;{activeEvent?.basicInfo?.eventName ?? ''}&quot;&nbsp;Event Priority Pass links
            </Typography>
          )}
        </div>
        <div className={s.titleButton}>
          <Button
            type="primary"
            size="large"
            onClick={showModal}
            style={{ fontSize: 13, paddingLeft: 40, paddingRight: 40 }}>
            + CREATE NEW
          </Button>
        </div>
      </div>
      <Label>
        This window is used to allow users, that are not managers on an event, to buy specific
        ticket types that can only be accessible by Event Managers. <br></br>Once an event link is
        created an email is sent to the user with the link to use to buy the tickets. The link can
        only be used once.
      </Label>
      <div style={{ width: '100%' }}>
        <Form className={s.filter} form={searchForm}>
          <div style={{ width: '100%' }}>
            <Label>Searcy by name or email</Label>
            <Form.Item name="term" className={s.filterItem}>
              <Input name="term" placeholder="Searcy by name or email" />
            </Form.Item>
          </div>
          <div style={{ width: '100%' }}>
            <Label>Status</Label>
            <Form.Item
              name="status"
              className={s.filterItem}
              initialValue={PRIORITY_PASS_STATUS_ENUM.ALL}>
              <Select
                style={{ height: 41 }}
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e);
                }}
                options={[
                  { label: 'All', value: PRIORITY_PASS_STATUS_ENUM.ALL },
                  { label: 'Created', value: PRIORITY_PASS_STATUS_ENUM.CREATED },
                  { label: 'Used', value: PRIORITY_PASS_STATUS_ENUM.USED },
                  { label: 'Disabled', value: PRIORITY_PASS_STATUS_ENUM.DISABLED },
                ]}
              />
            </Form.Item>
          </div>
        </Form>
      </div>
      <div className={s.content}>
        <div className={s.priorityPassList}>
          {fetching || isFetching ? (
            <div className={s.emptyNotificatoins}>
              <Spin indicator={<EventLoadingState size={250} />} />
            </div>
          ) : !priorityPasses || priorityPasses.length === 0 ? (
            <div className={s.emptyNotificatoins}>
              <div className={s.iconWrapper}>
                <Icon name="bell" color="grey" size={32} />
              </div>
              <Typography color="grey">You have no priority passes for this event yet</Typography>
              <Button
                type="primary"
                size="large"
                onClick={showModal}
                style={{ fontSize: 13, paddingTop: 9, paddingLeft: 40, paddingRight: 40 }}>
                + CREATE NEW
              </Button>
            </div>
          ) : (
            <>
              <HorizontalScrollWithArrows>
                <Table
                  style={{ marginTop: 16, cursor: 'pointer' }}
                  dataSource={priorityPasses}
                  rowKey="id"
                  expandable={{
                    expandIcon: () => null,
                    expandedRowRender: (record) => (
                      <SmoothCollapse isOpen={true} priorityPass={record} />
                    ),
                    expandedRowKeys: openPanels,
                    onExpand: (expanded, record) => {
                      setOpenPanels((prev) =>
                        expanded ? [...prev, record.id] : prev.filter((id) => id !== record.id),
                      );
                    },
                    rowExpandable: () => true,
                  }}
                  columns={[
                    {
                      title: '',
                      key: 'copy',
                      render: (_, record) => (
                        <Tooltip
                          placement="top"
                          title="Copy the link and share it with the person you want to send it to.">
                          <Button
                            type="link"
                            className="no-row-click"
                            style={{ padding: 0 }}
                            onClick={() => handleCopy(record?.id)}>
                            <CopyOutlined style={{ fontSize: 16 }} />
                          </Button>
                        </Tooltip>
                      ),
                    },
                    {
                      title: '',
                      key: 'toggle',
                      render: (_, record) => {
                        const isOpen = openPanels.includes(record.id);
                        return (
                          <Tooltip placement="top" title="View the tickets this link applies to.">
                            <Button
                              className="no-row-click"
                              type="link"
                              style={{ padding: 0 }}
                              onClick={() =>
                                setOpenPanels((prev) =>
                                  isOpen
                                    ? prev.filter((id) => id !== record.id)
                                    : [...prev, record.id],
                                )
                              }>
                              {isOpen ? (
                                <EyeInvisibleOutlined style={{ fontSize: 16 }} />
                              ) : (
                                <EyeOutlined style={{ fontSize: 16 }} />
                              )}
                            </Button>
                          </Tooltip>
                        );
                      },
                    },
                    {
                      title: 'Email',
                      dataIndex: 'email',
                      key: 'email',
                      onCell: (record) => ({
                        onClick: () => {
                          setDetailOpen(true);
                          setPriorityDetail(record);
                        },
                        style: { cursor: 'pointer' },
                      }),
                    },
                    {
                      title: 'Full Name',
                      dataIndex: 'fullName',
                      key: 'fullName',
                      onCell: (record) => ({
                        onClick: () => {
                          setDetailOpen(true);
                          setPriorityDetail(record);
                        },
                        style: { cursor: 'pointer' },
                      }),
                    },
                    {
                      title: 'Status',
                      dataIndex: 'status',
                      key: 'status',
                      onCell: (record) => ({
                        onClick: () => {
                          setDetailOpen(true);
                          setPriorityDetail(record);
                        },
                        style: { cursor: 'pointer' },
                      }),
                      render: (value: string) =>
                        value.charAt(0).toUpperCase() + value.slice(1).toLowerCase(),
                    },
                    {
                      title: 'Order No',
                      dataIndex: ['order', 'orderNo'],
                      key: 'orderNo',
                      onCell: (record) => ({
                        onClick: () => {
                          setDetailOpen(true);
                          setPriorityDetail(record);
                        },
                        style: { cursor: 'pointer' },
                      }),
                    },

                    {
                      title: 'Created by',
                      dataIndex: ['createdBy', 'email'],
                      key: 'createdBy',
                      onCell: (record) => ({
                        onClick: () => {
                          setDetailOpen(true);
                          setPriorityDetail(record);
                        },
                        style: { cursor: 'pointer' },
                      }),
                    },

                    {
                      title: 'Never Expires',
                      dataIndex: 'neverExpires',
                      key: 'neverExpires',
                      onCell: (record) => ({
                        onClick: () => {
                          setDetailOpen(true);
                          setPriorityDetail(record);
                        },
                        style: { cursor: 'pointer' },
                      }),
                      render: (value) => (value ? 'True' : 'False'),
                    },
                    {
                      title: 'Actions',
                      key: 'dropdown',
                      render: (_, record) => (
                        <Dropdown
                          className="no-row-click"
                          items={DROPDOWN_ITEMS}
                          placement="bottomRight"
                          handleItemClick={(event) => handleClickDropdownItems(event, record)}
                          open={openedDropdown === record.id}
                          onOpenChange={(open) => setOpenedDropdown(open ? record.id : null)}>
                          <div className={s.dropdownButton}>
                            <Icon name="verticalDots" color="#7d7da2" />
                          </div>
                        </Dropdown>
                      ),
                    },
                  ]}
                />
              </HorizontalScrollWithArrows>
              <Pagination
                totalElements={fetchedData?.totalRecords}
                showedElements={showedElements}
                currentPage={requestFilter?.pageNumber || 0}
                total={fetchedData?.totalPages}
                handlePageChange={handlePageChange}
                defaultPageSize={10}
                pageSizeOptions={[
                  { value: 10, label: '10' },
                  { value: 20, label: '20' },
                  { value: 30, label: '30' },
                ]}
                handleSizeChange={(size) =>
                  setRequestFilter((prevValue) => ({ ...prevValue, pageSize: size }))
                }
              />
            </>
          )}
        </div>
      </div>

      <Modal open={open} onOk={handleOk} handleClose={handleCancel} footer={null}>
        <Modal.Header>
          <Typography type="h3" weight={700}>
            {!editingPriorityPass ? 'Create' : 'Edit'} priority pass
          </Typography>
        </Modal.Header>
        <Label>
          Once the Pass is created an email is sent to the user with the Link to access the Ticket
          Type/s chosen. This action cannot be undone.
        </Label>
        <Form className={s.form} form={form} onFinish={handleSubmit}>
          {!editingPriorityPass && (
            <div>
              <Label>Email</Label>
              <Form.Item name="email" validateFirst rules={[formRules.required, formRules.email]}>
                <Input name="email" placeholder="Type email" />
              </Form.Item>
            </div>
          )}
          {!editingPriorityPass && (
            <div>
              <Label required>Full Name</Label>
              <Form.Item name="fullName" rules={[formRules.required]}>
                <Input name="fullName" placeholder="Type full name" />
              </Form.Item>
            </div>
          )}
          <div>
            <Label required>Ticket Types</Label>
            <Form.Item name="ticketTypes" rules={[formRules.required]}>
              <Select style={{ width: '100%', height: 38 }} options={ticketTypesOptions} />
            </Form.Item>
          </div>
          <div style={{ width: '100%' }}>
            <Label required>Quantity</Label>
            <Form.Item fullWidth name="qty" rules={[formRules.required]}>
              <Input.Number
                min={0}
                style={{ width: '100%' }}
                name="qty"
                placeholder="Type quantity"
              />
            </Form.Item>
          </div>
          {editingPriorityPass && (
            <div>
              <Label required>Status</Label>
              <Form.Item name="status" rules={[formRules.required]}>
                <Select
                  style={{ width: '100%', height: 38 }}
                  options={[
                    { label: 'Created', value: 'created' },
                    { label: 'Used', value: 'used' },
                    { label: 'Disabled', value: 'disabled' },
                  ]}
                />
              </Form.Item>
            </div>
          )}
          <div>
            <Typography type="main" weight={700} className={s.noMarginLable}>
              Never Expires?
            </Typography>
            <Form.Item
              name="neverExpires"
              initialValue={false} // or false, depending on default
            >
              <Switch />
            </Form.Item>
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginTop: 8,
              gap: 8,
              width: '100%',
            }}>
            <Button
              type="primary"
              size="large"
              loading={creating || editing}
              htmlType="submit"
              style={{ fontSize: 13, paddingLeft: 40, paddingRight: 40 }}>
              {!editingPriorityPass ? 'CREATE' : 'EDIT'}
            </Button>
            <Button
              type="link"
              size="large"
              style={{ fontSize: 13, color: '#7F86A4' }}
              onClick={handleCancel}>
              CANCEL
            </Button>
          </div>
        </Form>
      </Modal>
      <Modal
        centered
        closable={{ 'aria-label': 'Custom Close Button' }}
        open={detailOpen}
        onOk={handleDetailOk}
        handleClose={handleDetailCancel}
        className={s.responsiveModal}
        footer={null}>
        <Modal.Header>
          <Typography type="h3" weight={700}>
            Priority Pass info
          </Typography>
        </Modal.Header>
        <div className={s.detailContent}>
          <div className={s.detailItem}>
            <Typography type="label">Email</Typography>
            <Typography type="main">{priorityDetail?.email}</Typography>
          </div>
          <div className={s.detailItem}>
            <Typography type="label">Full Name</Typography>
            <Typography type="main">{priorityDetail?.fullName}</Typography>
          </div>
          <div className={s.detailItem}>
            <Typography type="label">Status</Typography>
            <Typography type="main">{toSentenceCase(priorityDetail?.status)}</Typography>
          </div>
          <div className={s.detailItem}>
            <Typography type="label">Order No</Typography>
            <Typography type="main">{priorityDetail?.order?.orderNo}</Typography>
          </div>
          <div className={s.detailItem}>
            <Typography type="label">Ticket Type</Typography>
            <Typography type="main">{priorityDetail?.ticketTypesAllowed[0].name}</Typography>
          </div>
          <div className={s.detailItem}>
            <Typography type="label">Qty Allowed</Typography>
            <Typography type="main">{priorityDetail?.ticketTypesAllowed[0].qty}</Typography>
          </div>
          <div className={s.detailItem}>
            <Typography type="label">Created At</Typography>
            <Typography type="main">{formatDate(priorityDetail?.createdAt as string)}</Typography>
          </div>
          <div className={s.detailItem}>
            <Typography type="label">Created By</Typography>
            <Typography type="main">{priorityDetail?.createdBy?.email}</Typography>
          </div>
          <div className={s.detailItem}>
            <Typography type="label">Used At</Typography>
            <Typography type="main">{priorityDetail?.usedAt}</Typography>
          </div>
          <div className={s.detailItem}>
            <Typography type="label">Used From</Typography>
            <Typography type="main">{priorityDetail?.usedFrom}</Typography>
          </div>

          <div className={s.detailItem}>
            <Typography type="label">Never Expires?</Typography>
            <Typography type="main">{priorityDetail?.neverExpires ? 'True' : 'False'}</Typography>
          </div>
          <div className={s.detailItem}></div>
        </div>
      </Modal>
    </div>
  );
};

const SmoothCollapse = ({ isOpen, priorityPass }) => {
  return (
    <div className={`${s.SmoothCollapse} ${isOpen ? s.open : ''}`}>
      <Row gutter={[0, 8]} className={s.rowStyle}>
        <Col span={16}>
          <div className="title">Ticket Type</div>
        </Col>
        <Col span={8}>
          <div className="title">Qty Allowed</div>
        </Col>
      </Row>

      <Row gutter={[0, 8]} className={s.rowContentStyle}>
        <Col span={16}>
          <div className="value">{priorityPass?.ticketTypesAllowed[0].name}</div>
        </Col>
        <Col span={8}>
          <div className="value">{priorityPass?.ticketTypesAllowed[0].qty}</div>
        </Col>
      </Row>
    </div>
  );
};

export default PriorityPass;
